import * as React from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/catalyst/input';

interface SaveFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export const SaveFilterDialog: React.FC<SaveFilterDialogProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [filterName, setFilterName] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = React.useCallback(async () => {
    if (!filterName.trim()) return;

    try {
      setIsSaving(true);
      await onSave(filterName.trim());
      setFilterName('');
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save filter:', error);
    } finally {
      setIsSaving(false);
    }
  }, [filterName, onSave, onClose]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && filterName.trim()) {
        handleSave();
      }
    },
    [filterName, handleSave],
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Save Filter</DialogTitle>
      <DialogDescription>
        Save the current filter settings for quick access later.
      </DialogDescription>
      <DialogBody>
        <div className="space-y-4">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Name
            </label>
            <Input
              id="filter-name"
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Open Unlinked Tasks"
              autoFocus
              disabled={isSaving}
            />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!filterName.trim() || isSaving}>
          {isSaving ? 'Saving...' : 'Save Filter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
